import {
  asClause,
  ensureTerminal,
  properWords,
  stripEnd,
  tidy,
  tidySeams,
} from "@/lib/tool-kit/essay/sentence";

export interface PeelInput {
  evidence: string;
  explanation: string;
  link: string;
  point: string;
}

export interface PeelTransitions {
  forExample: string;
  thisShows: string;
  ultimately: string;
}

const ENGLISH = /^en(?:-|$)/i;

/**
 * An author-date citation at the start of the evidence: "Smith (2020) found…",
 * "Smith and Jones, 2020, argue…", "Okafor et al. (2019) report…". The author's capital
 * is a name's, so it stays even though no other word of the text repeats it.
 */
const CITATION_LEAD =
  /^[\s"'“‘([]*\p{Lu}[\p{L}'’.-]*(?:\s+(?:and|&)\s+\p{Lu}[\p{L}'’-]*|\s+et al\.?)?\s*,?\s*\(?\d{4}/u;

/**
 * Builds the four-sentence PEEL paragraph. The point opens the paragraph and the other
 * three parts move into the middle of a transition ("For example, …"), so in English
 * their first letter is lowered only when the first word does not read as a name (NASA,
 * I, a run of capitalised words, a word the writer capitalises elsewhere, an author in
 * "Smith (2020)"). Other locales keep the text as typed, because lowering the first
 * letter would break German nouns and names in every language.
 */
export function buildPeel(
  input: PeelInput,
  transitions: PeelTransitions,
  locale: string
): string {
  const proper = properWords([
    input.point,
    input.evidence,
    input.explanation,
    input.link,
  ]);
  const clause = (text: string) => {
    if (!ENGLISH.test(locale) || CITATION_LEAD.test(text)) {
      return stripEnd(tidy(text));
    }
    return asClause(text, proper);
  };
  return [
    ensureTerminal(tidy(input.point)),
    tidySeams(`${transitions.forExample}${clause(input.evidence)}.`),
    tidySeams(`${transitions.thisShows}${clause(input.explanation)}.`),
    tidySeams(`${transitions.ultimately}${clause(input.link)}.`),
  ].join(" ");
}
